import { DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { IRowNode, SelectionChangedEvent } from 'ag-grid-community';
import { filter, Observable, take } from 'rxjs';
import { MileageHistoryGridService } from './mileage-history-grid.service';
import { VehiclesTabsService } from '@pages/vehicles-page/services/vehicles-tabs.service';

@Component({
  selector: 'app-mileage-history-grid',
  templateUrl: './mileage-history-grid.component.html',
  styleUrls: ['./mileage-history-grid.component.scss'],
  providers: [MileageHistoryGridService, GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MileageHistoryGridComponent implements OnInit {
  private readonly gridService = inject(MileageHistoryGridService);
  private gridOptionsService = inject(GridOptionsService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private vehicleInfoService = inject(VehiclesTabsService);

  @Input() entityId$: Observable<string | null>;
  gridOptions: GridOptions | undefined;
  actions = signal<DvToolbarAction[]>([]);

  private canEdit: boolean;
  private isEntityDeleted: boolean;
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.actions.set(this.gridService.getGridActions());
    this.listenToEntityChange();

    this.permissionsService
      .hasPermissions([RolesAndPermissions.VehicleAdjustMileageEdit])
      .pipe(take(1))
      .subscribe(hasPermissions => (this.canEdit = hasPermissions));
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    const selectedNode = api.getSelectedNodes()[0] as IRowNode | undefined;

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'delete') {
          return {
            ...action,
            disabled: !this.canEdit || !selectedNode || this.isEntityDeleted,
          };
        }
        return action;
      })
    );
  }

  private listenToEntityChange(): void {
    this.entityId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(entityId => !!entityId)
      )
      .subscribe(entityId => {
        this.isEntityDeleted = false;
        if (entityId !== NEW_ENTITY_ROUTE_PARAM) {
          this.vehicleInfoService
            .getExtraVehicleData(entityId)
            .pipe(take(1))
            .subscribe(({ ShowRecord }) => {
              if (!ShowRecord) {
                this.actions.update(actions =>
                  actions.map(action => {
                    if (action.icon === 'delete') {
                      return { ...action, disabled: true };
                    }
                    return action;
                  })
                );
              }
              this.isEntityDeleted = !ShowRecord;
            });
        }
        if (!this.gridOptions) {
          this.gridOptions = {
            ...this.gridOptionsService.clientSide(
              this.gridService.colDefs,
              Entity.MileageAdjustment,
              this.getFilterModel(entityId!)
            ),
          };
        }
        this.gridOptions.context.extraFilter = this.getFilterModel(entityId!);
        this.gridOptions.context.loadClientSideData?.(
          this.getFilterModel(entityId!),
          true
        );
      });
  }

  private getFilterModel(entityId: string): FilterModelWithKeys {
    return {
      VehicleID: {
        filterType: 'number',
        type: 'equals',
        filter: entityId,
      },
    };
  }
}
